/**
 * The post-schedule watchdog.
 *
 * WHAT THIS IS
 *   A pure evaluator: given the server-provided expected-start deadline, the
 *   last progress instant and an injectable clock, say whether the card is `ok`
 *   or `stalled`, and whether that verdict is terminal (the 5-minute budget is
 *   spent, so stop retrying and offer "Retry all").
 *
 * WHY IT EXISTS (evidence, 2026-09-05 Mongo write-stall incident)
 *   There is no post-schedule watchdog on the client at all. The only two
 *   watchdogs are PRE-schedule (a 60 s log-only stuck-at-0% check and a 120 s
 *   upload dialog), no timer transitions any card state, and the pod poll never
 *   gives up on a non-terminal card (CORE-PIPELINE-REFERENCE §10, correction
 *   22). During the stall, workflows sat in PROCESSING/STARTED with no
 *   `scheduled_tasks` row — 376 such rows over 14 days — and the tab spun
 *   forever with no way for the user to learn that nothing had started.
 *
 *   The deadline is SERVER-PROVIDED on purpose: the client cannot know queue
 *   depth, and a hard-coded local timeout would fire during ordinary busy
 *   periods. Measured pickup latency is p50 0.92 s / p90 1.44 s / p99 8.1 s /
 *   max 86 s, so any sane client-side constant would be either far too tight or
 *   useless. `expectedStartAtMs` comes from the schedule response.
 *
 *   Deliberately NOT a "no progress for N seconds" check: a long conversion can
 *   legitimately be quiet. This watchdog only answers "did work ever START".
 *
 * PLAN SECTION
 *   MONGO-STALL-PLAN-v5.2.md §4.3 ("Watchdog: server returns an expected-start
 *   deadline; card transitions to 'stalled' if nothing starts"), §4.4 (give up
 *   at 5 min); correction 22.
 *
 * WIRED 2026-09-05 behind `featureFlags.clientQueuedState`: `ScheduleCoordinator`
 * arms one timer per card from the schedule response's `expectedStartBySeconds`
 * (default 90 s) and asks this for the verdict when it fires.
 */

/** Plan §4.4 — matches SCHEDULE_RETRY_BUDGET_MS. */
export const STALLED_WATCHDOG_BUDGET_MS = 5 * 60 * 1_000;

export type StalledWatchdogStatus = 'ok' | 'stalled';

export interface StalledWatchdogInput {
    /** When this card's schedule attempt sequence began. */
    startedAtMs: number;
    /** Server-provided instant by which work should have started; null = unknown. */
    expectedStartAtMs?: number | null;
    /** Last progress instant seen, or null when nothing has ever started. */
    lastProgressAtMs?: number | null;
}

export interface StalledWatchdogVerdict {
    status: StalledWatchdogStatus;
    /**
     * True once the budget is spent: stop retrying and hand the user one
     * "Retry all". A terminal verdict is always `stalled`.
     */
    terminal: boolean;
    reason: string;
    /** Milliseconds left in the 5-minute budget (0 once spent). */
    remainingBudgetMs: number;
}

export interface StalledWatchdogOptions {
    /** Injectable clock; defaults to `Date.now`. */
    now?: () => number;
    /** Override the give-up budget. */
    budgetMs?: number;
}

export class StalledWatchdog {
    private readonly now: () => number;
    private readonly budgetMs: number;

    constructor(options: StalledWatchdogOptions = {}) {
        this.now = options.now ?? (() => Date.now());
        this.budgetMs = options.budgetMs ?? STALLED_WATCHDOG_BUDGET_MS;
    }

    check(input: StalledWatchdogInput): StalledWatchdogVerdict {
        const nowMs = this.now();
        const { startedAtMs } = input;
        const expectedStartAtMs = input.expectedStartAtMs ?? null;
        const lastProgressAtMs = input.lastProgressAtMs ?? null;

        const elapsed = nowMs - startedAtMs;
        const remainingBudgetMs = Math.max(0, this.budgetMs - elapsed);
        const budgetSpent = remainingBudgetMs === 0;

        // Work started: nothing to watch. Progress wins over the budget — a job
        // that is running must never be declared stalled just because it is slow.
        if (lastProgressAtMs !== null
            && (expectedStartAtMs === null || lastProgressAtMs >= expectedStartAtMs)) {
            return {
                status: 'ok',
                terminal: false,
                reason: 'progress-observed',
                remainingBudgetMs,
            };
        }

        if (budgetSpent) {
            return {
                status: 'stalled',
                terminal: true,
                reason: 'budget-exhausted',
                remainingBudgetMs: 0,
            };
        }

        // No server deadline: nothing to judge against until the budget runs out.
        if (expectedStartAtMs === null) {
            return {
                status: 'ok',
                terminal: false,
                reason: 'no-expected-start-deadline',
                remainingBudgetMs,
            };
        }

        if (nowMs >= expectedStartAtMs) {
            return {
                status: 'stalled',
                terminal: false,
                reason: 'expected-start-deadline-passed',
                remainingBudgetMs,
            };
        }

        return {
            status: 'ok',
            terminal: false,
            reason: 'before-expected-start',
            remainingBudgetMs,
        };
    }
}

export function createStalledWatchdog(options?: StalledWatchdogOptions): StalledWatchdog {
    return new StalledWatchdog(options);
}
